import React, { useState, useEffect } from 'react';
import { format, differenceInMinutes, differenceInHours } from 'date-fns';
import { 
  Clock, 
  ChevronDown, 
  ChevronUp, 
  CheckCircle2, 
  XCircle, 
  ChefHat, 
  Package, 
  Truck, 
  FileText, 
  RotateCcw,
  ShoppingBag,
  Utensils
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { useNavigate } from 'react-router-dom';
import MoneyUtils from '../../../utils/moneyUtils';
import { orderUtils } from '../../../utils/orderUtils';
import { useRestaurantContext } from '../../../context/RestaurantContext';

function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const OrderTimer = ({ startTime, orderStatus, accentColor }) => {
  const [elapsed, setElapsed] = useState('');
  const [urgencyClass, setUrgencyClass] = useState('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const start = new Date(startTime);
      const diffMin = differenceInMinutes(now, start);
      const diffHour = differenceInHours(now, start);

      if (diffMin < 15) {
        setUrgencyClass('text-blue-700 bg-blue-50 border-blue-200/80');
      } else if (diffMin < 30) {
        setUrgencyClass('text-amber-700 bg-amber-50 border-amber-200/80');
      } else {
        setUrgencyClass('text-rose-700 bg-rose-50 border-rose-200/80');
      }

      if (diffHour > 0) {
        setElapsed(`${diffHour}h ${diffMin % 60}m`);
      } else {
        setElapsed(`${diffMin}m`);
      }
    };

    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, [startTime]);

  const isFinalized = ['completed', 'cancelled', 'failed', 'delivered'].includes(orderStatus?.toLowerCase());
  if (isFinalized) return null;

  return (
    <div className={cn(
      "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-bold tracking-tight shadow-2xs",
      urgencyClass
    )}>
      <Clock size={10} strokeWidth={2.5} />
      <span>{elapsed}</span>
    </div>
  );
};

const AnimatedStatusLabel = ({ status, label }) => {
  const isFinalized = ['completed', 'delivered', 'cancelled', 'failed'].includes(status?.toLowerCase());
  const isDynamic = !isFinalized;
  const [showDots, setShowDots] = useState(false);

  useEffect(() => {
    if (!isDynamic) return;
    let timeoutId;
    const scheduleNext = (currentDotsState) => {
      // 2000ms for dots, 5000ms for status text
      const delay = currentDotsState ? 2000 : 5000;
      timeoutId = setTimeout(() => {
        setShowDots((prev) => {
          const next = !prev;
          scheduleNext(next);
          return next;
        });
      }, delay);
    };

    scheduleNext(false);
    return () => clearTimeout(timeoutId);
  }, [isDynamic]);

  if (!isDynamic) {
    return <span>{label}</span>;
  }

  return (
    <span className="relative inline-flex items-center justify-center">
      {/* Invisible anchor text locking container dimensions so it NEVER shrinks */}
      <span className="invisible opacity-0 select-none whitespace-nowrap pointer-events-none" aria-hidden="true">
        {label}
      </span>

      {/* Cross-fading layer occupying locked space */}
      <span className="absolute inset-0 flex items-center justify-center">
        <AnimatePresence mode="wait">
          {showDots ? (
            <motion.span
              key="dots"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: 'easeInOut' }}
              className="flex items-center justify-center gap-1"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80 animate-pulse" />
              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80 animate-pulse delay-100" />
              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80 animate-pulse delay-200" />
            </motion.span>
          ) : (
            <motion.span
              key="text"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.35, ease: 'easeInOut' }}
              className="whitespace-nowrap flex items-center justify-center"
            >
              {label}
            </motion.span>
          )}
        </AnimatePresence>
      </span>
    </span>
  );
};

const getStatusConfig = (status) => {
  switch (status?.toLowerCase()) {
    case 'pending':
      return {
        label: 'Awaiting Approval',
        bg: 'bg-amber-50 text-amber-800 border-amber-200',
        dot: 'bg-amber-500 animate-pulse',
        icon: <Clock size={12} strokeWidth={2.5} className="text-amber-600 animate-spin-slow" />
      };
    case 'confirmed':
    case 'processing':
      return {
        label: 'Accepted',
        bg: 'bg-blue-50 text-blue-800 border-blue-200',
        dot: 'bg-blue-500',
        icon: <CheckCircle2 size={12} strokeWidth={2.5} className="text-blue-600" />
      };
    case 'preparing':
      return {
        label: 'Preparing',
        bg: 'bg-indigo-50 text-indigo-800 border-indigo-200',
        dot: 'bg-indigo-500 animate-pulse',
        icon: <ChefHat size={12} strokeWidth={2.5} className="text-indigo-600" />
      };
    case 'ready_for_pickup':
      return {
        label: 'Ready for Pickup',
        bg: 'bg-emerald-50 text-emerald-800 border-emerald-200 shadow-sm',
        dot: 'bg-emerald-500 animate-ping',
        icon: <Package size={12} strokeWidth={2.5} className="text-emerald-600" />
      };
    case 'out_for_delivery':
      return {
        label: 'Out for Delivery',
        bg: 'bg-sky-50 text-sky-800 border-sky-200',
        dot: 'bg-sky-500',
        icon: <Truck size={12} strokeWidth={2.5} className="text-sky-600" />
      };
    case 'completed':
    case 'delivered':
      return {
        label: 'Completed',
        bg: 'bg-green-50 text-green-800 border-green-200',
        dot: 'bg-green-500',
        icon: <CheckCircle2 size={12} strokeWidth={2.5} className="text-green-600" />
      };
    case 'cancelled':
    case 'failed':
      return {
        label: 'Cancelled',
        bg: 'bg-rose-50 text-rose-800 border-rose-200',
        dot: 'bg-rose-500',
        icon: <XCircle size={12} strokeWidth={2.5} className="text-rose-600" />
      };
    default:
      return {
        label: status || 'Active',
        bg: 'bg-gray-100 text-gray-800 border-gray-200',
        dot: 'bg-gray-400',
        icon: <Clock size={12} strokeWidth={2.5} className="text-gray-500" />
      };
  }
};

const OrderCard = ({ order, isExpanded, onToggleExpand, onReorder }) => {
  const { theme } = useRestaurantContext();
  const accentColor = theme?.secondary || '#000000';
  const textOnAccent = theme?.primary || '#FFFFFF';
  const navigate = useNavigate();
  const [showAllItems, setShowAllItems] = useState(false);
  const restId = order.restaurant_id || order.restaurantId;

  const orderStatus = order.orderStatus || 'unknown';
  const orderTypeRaw = order.orderType || 'dine_in';
  const isDelivery = orderTypeRaw?.toLowerCase() === 'delivery';
  const isTakeaway = orderTypeRaw?.toLowerCase() === 'takeaway';

  const getServiceInfo = () => {
    if (isDelivery) return { icon: <Truck size={11} className="text-gray-500" />, label: 'Delivery' };
    if (isTakeaway) return { icon: <ShoppingBag size={11} className="text-gray-500" />, label: 'Takeaway' };
    return { 
      icon: <Utensils size={11} className="text-gray-500" />, 
      label: order.tableNumber ? orderUtils.formatTableDisplayName(order.tableNumber) : 'Dine In' 
    };
  };

  const serviceInfo = getServiceInfo();
  const shortOrderNumber = orderUtils.getShortCode(order.orderNumber) || order.id?.slice(0, 6);

  const formattedTime = order.createdAt || order.created_at 
    ? format(new Date(order.createdAt || order.created_at), 'hh:mm a') 
    : '';

  const displayDate = order.createdAt || order.created_at
    ? format(new Date(order.createdAt || order.created_at), 'dd MMM yyyy')
    : '';

  // Pricing calculation
  const itemsTotal = order.items?.reduce((sum, item) => {
    const addonTotal = item.addons?.reduce((aSum, addon) => aSum + (addon.price || 0) * (addon.quantity || 1), 0) || 0;
    const comboTotal = item.comboComponents?.reduce((cSum, comp) => cSum + (comp.extraCharge || comp.selectedOptionExtraPrice || 0), 0) || 0;
    return sum + ((item.price || 0) + addonTotal + comboTotal) * (item.quantity || 1);
  }, 0) || 0;

  const taxesAndCharges = order.taxesAndCharges || order.taxAmount || 0;
  const deliveryFee = order.deliveryFee || 0;
  const tipAmount = order.tipAmount || 0;
  const discountAmount = order.discountAmount || 0;
  const calculatedTotal = order.finalAmount || (itemsTotal + taxesAndCharges + deliveryFee + tipAmount - discountAmount);

  const isCompleted = ['completed', 'delivered'].includes(orderStatus?.toLowerCase());
  const statusConfig = getStatusConfig(orderStatus);

  // Settlement check directly from order paymentStatus
  const isSettled = order.paymentStatus?.toLowerCase() === 'paid' || order.paymentStatus?.toLowerCase() === 'completed';

  // Compact Payment Method formatting (e.g. CASH, UPI, ONLINE, SPLIT, COD)
  const getCompactPaymentLabel = () => {
    const raw = (order.paymentMethod || 'cod').toLowerCase();
    if (raw.includes('cash')) return 'CASH';
    if (raw.includes('upi')) return 'UPI';
    if (raw.includes('razorpay') || raw.includes('online') || raw.includes('stripe')) return 'ONLINE';
    if (raw.includes('split') || raw.includes('part')) return 'SPLIT';
    if (raw.includes('card')) return 'CARD';
    return raw.toUpperCase();
  };
  const compactPaymentMethod = getCompactPaymentLabel();

  const handleOpenDetails = (e) => {
    e?.stopPropagation();
    if (restId && order.id) {
      navigate(`/restaurant/${restId}/orders/${order.id}`);
    }
  };

  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="bg-white rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.06)] border border-gray-100 overflow-hidden hover:border-gray-200 transition-all w-full flex flex-col"
    >
      <div className="px-2.5 py-3 flex-1 flex flex-col gap-2">
        {/* Top Header: Order ID + Status Badge */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-1.5">
            <div 
              className="px-2 py-0.5 rounded-md font-black text-xs tracking-wider shadow-2xs"
              style={{ backgroundColor: `${accentColor}15`, color: accentColor }}
            >
              #{shortOrderNumber}
            </div>
            <span className="text-[10px] font-semibold text-gray-400 leading-none">
              {displayDate} • {formattedTime}
            </span>
          </div>

          <div 
            className={cn(
              "flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-bold border shrink-0",
              statusConfig.bg
            )}
          >
            {statusConfig.icon}
            <AnimatedStatusLabel status={orderStatus} label={statusConfig.label} />
          </div>
        </div>

        {/* Mini Meta Info Tags */}
        <div className="flex flex-wrap items-center gap-1 pt-0.5">
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-gray-50 border border-gray-200/60 text-[10px] font-semibold text-gray-700">
            {serviceInfo.icon}
            <span>{serviceInfo.label}</span>
          </span>

          <span className={cn(
            "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider border",
            isSettled 
              ? "bg-emerald-50 text-emerald-700 border-emerald-200/70" 
              : "bg-amber-50 text-amber-700 border-amber-200/70"
          )}>
            {isSettled ? `PAID • ${compactPaymentMethod}` : 'NOT PAID'}
          </span>

          <OrderTimer 
            startTime={order.createdAt || order.created_at} 
            orderStatus={orderStatus} 
            accentColor={accentColor}
          />
        </div>

        {/* Cancellation Reason Note */}
        {(orderStatus?.toLowerCase() === 'cancelled' || orderStatus?.toLowerCase() === 'failed') && (order.cancellationReason || order.cancellation_reason || order.reason) && (
          <div className="bg-rose-50/80 border border-rose-200/70 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5 text-[10px] text-rose-900">
            <span className="font-black text-rose-600 shrink-0 uppercase text-[9px] tracking-wider">Reason:</span>
            <span className="truncate font-semibold">{order.cancellationReason || order.cancellation_reason || order.reason}</span>
          </div>
        )}

        {/* Subtle Divider */}
        <div className="border-t border-gray-100 my-0.5" />

        {/* Ordered Items List */}
        <div className="space-y-1.5 flex-1">
          {(showAllItems ? order.items : order.items?.slice(0, 3))?.map((item, idx) => {
            const addons = item.addons || item.customizations?.addons || [];
            return (
              <div key={idx} className="flex flex-col text-xs">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-1.5 flex-1 pr-2">
                    <span 
                      className="min-w-[16px] h-4 px-1 rounded text-[9px] font-black flex items-center justify-center"
                      style={{ backgroundColor: `${accentColor}12`, color: accentColor }}
                    >
                      {item.quantity}x
                    </span>
                    <span className="font-bold text-gray-800 leading-tight text-[11px]">
                      {orderUtils.toTitleCase(item.name)}
                    </span>
                    {(item.portion?.name || item.portionName) && (
                      <span className="text-[9px] font-bold px-1 py-0.2 rounded bg-gray-100 text-gray-600">
                        {orderUtils.toTitleCase(item.portion?.name || item.portionName)}
                      </span>
                    )}
                  </div>
                  <span className="font-bold text-gray-900 shrink-0 text-[11px]">
                    {MoneyUtils.format((item.price || 0) * (item.quantity || 1))}
                  </span>
                </div>

                {/* Addon Pills */}
                {addons.length > 0 && (
                  <div className="pl-5 pt-0.5 flex flex-wrap gap-1 text-[9px] text-gray-500">
                    {addons.map((a, aIdx) => (
                      <span key={aIdx} className="text-gray-400">
                        +{orderUtils.capitalize(a.name)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Expand/Collapse Items Toggle */}
        {order.items?.length > 3 && (
          <button 
            onClick={() => setShowAllItems(!showAllItems)}
            className="text-[10px] font-bold text-gray-500 hover:text-gray-900 flex items-center gap-1 self-start pt-0.5 cursor-pointer"
          >
            {showAllItems ? (
              <><ChevronUp size={11} /> Show fewer items</>
            ) : (
              <><ChevronDown size={11} /> +{order.items.length - 3} more items</>
            )}
          </button>
        )}

        {/* Order Summary & Actions Footer */}
        <div className="pt-1.5 border-t border-gray-100 flex items-center justify-between">
          <div>
            <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400 block leading-none mb-0.5">
              Total ({(order.items || []).length} items)
            </span>
            <span className="text-xs font-black text-gray-900">
              {MoneyUtils.format(calculatedTotal)}
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            <button 
              onClick={handleOpenDetails}
              className="px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 text-[11px] font-bold text-gray-700 flex items-center gap-1 transition-all active:scale-95 cursor-pointer"
            >
              <FileText size={12} />
              <span>Details</span>
            </button>
          </div>
        </div>

        {/* Expanded Bill Details Breakdown */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden pt-1"
            >
              <div className="bg-gray-50/80 rounded-xl p-2 border border-gray-100 space-y-1 text-[10px]">
                <div className="flex justify-between text-gray-500">
                  <span>Items Subtotal</span>
                  <span className="font-semibold text-gray-700">{MoneyUtils.format(itemsTotal)}</span>
                </div>
                {taxesAndCharges > 0 && (
                  <div className="flex justify-between text-gray-500">
                    <span>Taxes & Charges</span>
                    <span className="font-semibold text-gray-700">{MoneyUtils.format(taxesAndCharges)}</span>
                  </div>
                )}
                {deliveryFee > 0 && (
                  <div className="flex justify-between text-gray-500">
                    <span>Delivery Fee</span>
                    <span className="font-semibold text-gray-700">{MoneyUtils.format(deliveryFee)}</span>
                  </div>
                )}
                {tipAmount > 0 && (
                  <div className="flex justify-between text-gray-500">
                    <span>Tip</span>
                    <span className="font-semibold text-gray-700">{MoneyUtils.format(tipAmount)}</span>
                  </div>
                )}
                {discountAmount > 0 && (
                  <div className="flex justify-between text-emerald-600 font-bold">
                    <span>Discount</span>
                    <span>-{MoneyUtils.format(discountAmount)}</span>
                  </div>
                )}
                {billingSummary?.refundSummary?.hasRefunds && (
                  <div className="flex justify-between text-rose-600 font-bold">
                    <span>Refunded (Credit Note)</span>
                    <span>-{MoneyUtils.format(billingSummary.refundSummary.totalCreditedAmount || billingSummary.refundSummary.totalRefundedAmount)}</span>
                  </div>
                )}
                <div className="pt-1 border-t border-gray-200/60 flex justify-between items-center font-bold text-[11px]">
                  <span className="text-gray-900">
                    {billingSummary?.bill?.billNumber ? `Bill #${billingSummary.bill.billNumber}` : 'Final Bill'}
                  </span>
                  <span className="text-gray-900 font-black">{MoneyUtils.format(calculatedTotal)}</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default OrderCard;